import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import ClientDeliveryAutomationPanel from './ClientDeliveryAutomationPanel';

interface ClientOption {
  id: string;
  name: string;
}

interface NotionUser {
  id: string;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
}

interface RunResult {
  success: boolean;
  project_page_url?: string;
  project_page_id?: string;
  milestones_created?: number;
  subtasks_created?: number;
  errors?: string[];
}

type PackageOption = 'Full Build' | 'Ads' | 'Client Delivery';

const PACKAGE_TEMPLATES: Record<PackageOption, string> = {
  'Full Build': 'Workflow Templates v3 (Optimized Onboarding)',
  'Ads': 'Client Onboarding Template — Ads (Optimized Onboarding)',
  'Client Delivery': '⚙️ Client Delivery Template V2',
};

const OnboardingAutomationPanel: React.FC = () => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientQuery, setClientQuery] = useState<string>('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientFieldRef = useRef<HTMLDivElement | null>(null);
  const [startDate, setStartDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [pkg, setPkg] = useState<PackageOption>('Full Build');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string>('');
  const [logLines, setLogLines] = useState<string[]>([]);
  const [savedRun, setSavedRun] = useState<{
    id?: number;
    package?: string;
    task_page_url?: string;
    created_at?: string;
    milestones_created?: number;
    subtasks_created?: number;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  interface RunRow {
    id: number;
    client_page_id: string;
    client_name: string;
    package?: string;
    task_page_url?: string;
    milestones_created: number;
    subtasks_created: number;
    status: string;
    created_at: string;
  }
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [deletingRunId, setDeletingRunId] = useState<number | null>(null);

  const [teams, setTeams] = useState<string[]>([]);
  const [users, setUsers] = useState<NotionUser[]>([]);
  // teamName -> [notion_user_id, ...]
  const [teamAssignees, setTeamAssignees] = useState<Record<string, string[]>>({});
  const [openTeamPicker, setOpenTeamPicker] = useState<string | null>(null);
  const teamPickerRef = useRef<HTMLDivElement | null>(null);

  const loadRuns = async () => {
    try {
      const resp = await api.get('/onboarding-automation/runs');
      setRuns(resp.data?.runs || []);
    } catch {
      // ignore
    }
  };
  const logEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingClients(true);
        const resp = await api.get('/onboarding-automation/clients');
        if (!cancelled) setClients(resp.data?.clients || []);
      } catch (err: any) {
        if (!cancelled) {
          setError('Failed to load clients: ' + (err.response?.data?.detail || err.message));
        }
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    };
    load();
    loadRuns();
    return () => { cancelled = true; };
  }, []);

  // The team list depends on which package template is selected — the Ads
  // template touches a different set of teams than Full Build. Refetch on
  // switch and clear any picks made for the previous template's teams.
  useEffect(() => {
    let cancelled = false;
    setTeamAssignees({});
    setOpenTeamPicker(null);
    if (pkg === 'Client Delivery') return;
    (async () => {
      try {
        const resp = await api.get('/onboarding-automation/teams', { params: { package: pkg } });
        if (cancelled) return;
        setTeams(resp.data?.teams || []);
        setUsers(resp.data?.users || []);
      } catch {
        // non-fatal — team picker just won't render
      }
    })();
    return () => { cancelled = true; };
  }, [pkg]);

  // Close the open team picker on outside click.
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (teamPickerRef.current && !teamPickerRef.current.contains(e.target as Node)) {
        setOpenTeamPicker(null);
      }
    };
    if (openTeamPicker) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
  }, [openTeamPicker]);

  const setTeamUser = (team: string, userId: string | null) => {
    // Single-select: only one person can own a team's tasks. The wire format
    // stays as a list so the backend signature is unchanged — we just send a
    // 0- or 1-element array.
    setTeamAssignees((prev) => ({ ...prev, [team]: userId ? [userId] : [] }));
    setOpenTeamPicker(null);
  };

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, clientQuery]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (clientFieldRef.current && !clientFieldRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // When a client is selected, look up the latest saved run for that client.
  useEffect(() => {
    if (!selectedClientId) {
      setSavedRun(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/onboarding-automation/latest-run/${selectedClientId}`);
        if (!cancelled) setSavedRun(resp.data?.run || null);
      } catch {
        if (!cancelled) setSavedRun(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  // Auto-scroll the log console to the bottom as new lines arrive.
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logLines]);

  const handleDelete = async () => {
    if (!savedRun?.id) return;
    const confirmed = window.confirm(
      'Archive the project + all milestones/sub-tasks in Notion?\n\n' +
      'They go to Notion\'s trash (recoverable for 30 days). ' +
      'The saved link is removed from this panel.'
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      const resp = await api.delete(`/onboarding-automation/run/${savedRun.id}`);
      setSavedRun(null);
      setResult(null);
      loadRuns();
      alert(
        `Archived ${resp.data?.archived ?? 0} Notion pages` +
        (resp.data?.failed ? `, ${resp.data.failed} failed` : '')
      );
    } catch (err: any) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteRow = async (runId: number) => {
    const confirmed = window.confirm(
      'Archive this onboarding (project + milestones + sub-tasks) in Notion?\n' +
      'They go to Notion\'s trash, recoverable for 30 days.'
    );
    if (!confirmed) return;
    setDeletingRunId(runId);
    try {
      const resp = await api.delete(`/onboarding-automation/run/${runId}`);
      if (savedRun?.id === runId) {
        setSavedRun(null);
        setResult(null);
      }
      await loadRuns();
      alert(
        `Archived ${resp.data?.archived ?? 0} Notion pages` +
        (resp.data?.failed ? `, ${resp.data.failed} failed` : '')
      );
    } catch (err: any) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleRun = async () => {
    if (!selectedClient) return;
    setRunning(true);
    setResult(null);
    setError('');
    setLogLines([]);
    try {
      const token = localStorage.getItem('auth_token') || '';
      const resp = await fetch('/api/onboarding-automation/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          client_id: selectedClient.id,
          client_name: selectedClient.name,
          start_date: startDate,
          package: pkg,
          // Drop empty team rows so backend only overrides the teams the
          // user explicitly picked someone for.
          team_assignees: Object.fromEntries(
            Object.entries(teamAssignees).filter(([, ids]) => (ids || []).length > 0)
          ),
        }),
      });
      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop() || '';
        for (const raw of parts) {
          const line = raw.trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (typeof evt.log === 'string') {
              setLogLines((prev) => [...prev, evt.log]);
            } else if (evt.final) {
              setResult(evt.final as RunResult);
              if (evt.final?.project_page_url && selectedClient) {
                // Refetch latest-run to get the DB id needed for delete.
                try {
                  const resp = await api.get(`/onboarding-automation/latest-run/${selectedClient.id}`);
                  setSavedRun(resp.data?.run || null);
                } catch {
                  setSavedRun({
                    task_page_url: evt.final.project_page_url,
                    created_at: new Date().toISOString(),
                    milestones_created: evt.final.milestones_created,
                    subtasks_created: evt.final.subtasks_created,
                  });
                }
                loadRuns();
              }
            }
          } catch {
            // ignore malformed line
          }
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const renderPackageSelector = (disabled: boolean) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Package template</label>
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
        {(Object.keys(PACKAGE_TEMPLATES) as PackageOption[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPkg(p)}
            disabled={disabled}
            className={`px-4 py-2 text-sm font-medium disabled:opacity-50 ${
              pkg === p
                ? 'bg-black text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } ${p !== 'Full Build' ? 'border-l border-gray-300' : ''}`}
          >
            {p === 'Ads' ? '🎯 Ads' : p}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Source: {PACKAGE_TEMPLATES[pkg]}
      </p>
    </div>
  );

  if (pkg === 'Client Delivery') {
    return <ClientDeliveryAutomationPanel renderPackageSelector={renderPackageSelector} />;
  }

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Onboarding Automation</h2>
      <p className="text-sm text-gray-600 mb-5">
        Pick a client and trigger the Notion onboarding flow — creates the parent project,
        copies template milestones, and links sub-tasks. The package selects which
        template is applied.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        {renderPackageSelector(running)}

        <div ref={clientFieldRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Client {clients.length > 0 && (
              <span className="text-xs font-normal text-gray-400">({clients.length})</span>
            )}
          </label>
          <div className="relative">
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => {
                setClientQuery(e.target.value);
                setShowClientDropdown(true);
                if (selectedClientId !== null) setSelectedClientId(null);
              }}
              onFocus={() => setShowClientDropdown(true)}
              placeholder={loadingClients ? 'Loading clients…' : 'Search clients…'}
              disabled={loadingClients || running}
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {clientQuery && (
              <button
                type="button"
                onClick={() => {
                  setClientQuery('');
                  setSelectedClientId(null);
                  setShowClientDropdown(true);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700"
                aria-label="Clear"
              >
                ×
              </button>
            )}
            {showClientDropdown && !running && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                {filteredClients.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {loadingClients ? 'Loading…' : 'No clients match.'}
                  </div>
                ) : (
                  filteredClients.map((c) => {
                    const isSel = c.id === selectedClientId;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedClientId(c.id);
                          setClientQuery(c.name);
                          setShowClientDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                          isSel ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-800'
                        }`}
                      >
                        {c.name}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          {selectedClient && (
            <p className="text-xs text-gray-500 mt-1">
              Selected: <span className="font-medium text-gray-700">{selectedClient.name}</span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project start date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={running}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Milestone due dates are computed as start date + each template's "Days After Project Start".
          </p>
        </div>

        {teams.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team assignees</label>
            <p className="text-xs text-gray-500 mb-2">
              Pick one person to own each team's tasks. The selected user replaces
              the template's default Assignee for any task tagged with that Team.
              Leave empty to keep the template's default.
            </p>
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
              {teams.map((team) => {
                const pickedId = (teamAssignees[team] || [])[0] || null;
                const pickedUser = pickedId ? users.find((x) => x.id === pickedId) : null;
                const isOpen = openTeamPicker === team;
                return (
                  <div key={team} className="px-3 py-2 flex items-start gap-3">
                    <div className="w-40 shrink-0 text-sm font-medium text-gray-800 pt-1">{team}</div>
                    <div className="flex-1 min-w-0 relative" ref={isOpen ? teamPickerRef : null}>
                      <button
                        type="button"
                        onClick={() => setOpenTeamPicker(isOpen ? null : team)}
                        disabled={running}
                        className="w-full text-left border border-gray-300 rounded px-2 py-1.5 text-sm bg-white hover:bg-gray-50 disabled:opacity-50 min-h-[34px]"
                      >
                        {!pickedId ? (
                          <span className="text-gray-400">Use template default…</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                            {pickedUser?.name || pickedId}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setTeamUser(team, null); }}
                              className="text-blue-500 hover:text-blue-700"
                              aria-label="Clear selection"
                            >
                              ×
                            </button>
                          </span>
                        )}
                      </button>
                      {isOpen && (
                        <div className="absolute left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-20">
                          {users.length === 0 && (
                            <div className="p-3 text-xs text-gray-500">No workspace users found.</div>
                          )}
                          <button
                            type="button"
                            onClick={() => setTeamUser(team, null)}
                            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${!pickedId ? 'bg-blue-50/50' : ''}`}
                          >
                            <input
                              type="radio"
                              readOnly
                              checked={!pickedId}
                              className="pointer-events-none"
                            />
                            <span className="flex-1 min-w-0 truncate text-gray-500 italic">
                              Use template default
                            </span>
                          </button>
                          {users.map((u) => {
                            const checked = pickedId === u.id;
                            return (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => setTeamUser(team, u.id)}
                                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${checked ? 'bg-blue-50/50' : ''}`}
                              >
                                <input
                                  type="radio"
                                  readOnly
                                  checked={checked}
                                  className="pointer-events-none"
                                />
                                <span className="flex-1 min-w-0 truncate">{u.name || u.email || u.id}</span>
                                {u.email && (
                                  <span className="text-xs text-gray-400 truncate">{u.email}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {savedRun?.task_page_url && !running && (
          <div className="text-xs bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-gray-700">
                  Latest onboarding for this client
                  {savedRun.package ? ` (${savedRun.package})` : ''}:
                </div>
                <a
                  href={savedRun.task_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline break-all"
                >
                  {savedRun.task_page_url}
                </a>
                {(savedRun.milestones_created !== undefined || savedRun.subtasks_created !== undefined) && (
                  <div className="text-gray-500 mt-0.5">
                    {savedRun.milestones_created ?? 0} milestones · {savedRun.subtasks_created ?? 0} sub-tasks
                    {savedRun.created_at ? ` · ${new Date(savedRun.created_at).toLocaleString()}` : ''}
                  </div>
                )}
              </div>
              {savedRun.id && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-white hover:bg-red-50 border border-red-200 rounded disabled:opacity-50"
                  title="Archive in Notion (soft-delete, recoverable from Notion trash for 30 days)"
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="pt-2">
          <button
            onClick={handleRun}
            disabled={!selectedClient || running}
            className="px-4 py-2 text-sm font-medium text-white bg-black hover:bg-gray-800 disabled:opacity-50 rounded-md"
          >
            {running ? 'Running…' : 'Run Onboarding Automation'}
          </button>
        </div>
      </div>

      {(running || logLines.length > 0) && (
        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700 mb-1">Progress</div>
          <div className="bg-gray-900 text-gray-100 text-xs font-mono rounded-md p-3 max-h-72 overflow-y-auto">
            {logLines.length === 0 && running && (
              <div className="text-gray-400">Connecting…</div>
            )}
            {logLines.map((line, i) => (
              <div key={i} className="whitespace-pre-wrap break-words">{line}</div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md text-sm">
          <div className="font-medium text-green-800 mb-2">Onboarding created.</div>
          <ul className="text-gray-700 space-y-1">
            {result.project_page_url && (
              <li>
                Project page:{' '}
                <a
                  href={result.project_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline break-all"
                >
                  {result.project_page_url}
                </a>
              </li>
            )}
            {typeof result.milestones_created === 'number' && (
              <li>Milestones created: {result.milestones_created}</li>
            )}
            {typeof result.subtasks_created === 'number' && (
              <li>Sub-tasks created: {result.subtasks_created}</li>
            )}
            {result.errors && result.errors.length > 0 && (
              <li className="text-amber-700">
                Warnings: {result.errors.length} (see server logs for details)
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Recent runs</h3>
          {runs.length > 0 && (
            <span className="text-xs text-gray-400">{runs.length}</span>
          )}
        </div>
        {runs.length === 0 ? (
          <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-md px-3 py-3">
            No onboarding runs yet. Pick a client above and click Run.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-100">
            {runs.map((r) => (
              <div key={r.id} className="px-3 py-2 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {r.client_name}
                    {r.package && (
                      <span
                        className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                          r.package === 'Ads'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {r.package}
                      </span>
                    )}
                    {r.status !== 'completed' && (
                      <span className="ml-2 text-xs text-amber-700 font-normal">({r.status})</span>
                    )}
                  </div>
                  {r.task_page_url ? (
                    <a
                      href={r.task_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-700 underline break-all"
                    >
                      {r.task_page_url}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400">no project URL</span>
                  )}
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.milestones_created} milestones · {r.subtasks_created} sub-tasks
                    {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteRow(r.id)}
                  disabled={deletingRunId === r.id}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-white hover:bg-red-50 border border-red-200 rounded disabled:opacity-50"
                  title="Archive in Notion + remove from this list"
                >
                  {deletingRunId === r.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingAutomationPanel;
